<template>
  <!-- The welcome wizard's step 3 (Muhammad, 5 Oct 2026): the library by
       category. Category tiles first; opening one shows its courses to tick;
       search looks across the whole library. Selection lives in the wizard
       (it is saved with the progress): this emits `toggle` and `clear`. -->
  <div class="starter-picker">
    <div class="course-toolbar">
      <v-text-field
        v-model="search"
        placeholder="Search all courses"
        prepend-inner-icon="fa-search"
        outlined
        dense
        hide-details
        clearable
        class="course-search"
      />
      <span class="course-toolbar__count">
        {{ selected.length }} selected
        <button
          v-if="selected.length"
          type="button"
          class="picker-link ml-2"
          @click="$emit('clear')"
        >
          Clear
        </button>
      </span>
    </div>

    <div v-if="showingCategories" class="category-grid">
      <button
        v-for="group in groups"
        :key="group.id"
        type="button"
        class="category-tile"
        @click="openCategoryId = group.id"
      >
        <span class="category-tile__name">{{ group.name }}</span>
        <!-- Count and picks share a line, so every tile is the same height. -->
        <span class="category-tile__meta">
          <span class="category-tile__count">{{ countLabel(group.courses.length) }}</span>
          <span v-if="selectedIn(group)" class="category-tile__selected">
            {{ selectedIn(group) }} selected
          </span>
        </span>
      </button>
    </div>

    <template v-else>
      <div v-if="hasCategories && openGroup && !searching" class="category-head">
        <button type="button" class="picker-link category-back" @click="openCategoryId = null">
          <v-icon x-small color="#08a0a8" class="mr-1">fa-arrow-left</v-icon>
          All categories
        </button>
        <h3 class="category-head__title">
          {{ openGroup.name }} <span>({{ openGroup.courses.length }})</span>
        </h3>
      </div>
      <!-- Six cards in view (three rows of two), the rest scroll. -->
      <div class="course-scroll">
        <div class="course-grid">
          <div
            v-for="course in visibleCourses"
            :key="course.id"
            class="course-card"
            :class="{ 'is-selected': selectedIds[course.id] }"
            @click="$emit('toggle', course)"
          >
            <div class="course-card__badge">{{ course.tag }}</div>
            <h3>{{ course.name }}</h3>
            <p>{{ searching ? categoryName(course) : "Course" }}</p>
            <v-icon v-if="selectedIds[course.id]" class="course-card__check" color="primary">
              fa-check-circle
            </v-icon>
          </div>
        </div>
        <p v-if="searching && !visibleCourses.length" class="picker-hint">
          No course matches “{{ searchText }}”.
        </p>
      </div>
    </template>

    <p class="picker-hint mt-4">{{ hint }}</p>
  </div>
</template>

<script>
import { groupCoursesByCategory } from "@/utils/courseCategories";

export default {
  name: "StarterCoursePicker",
  props: {
    // [{ id, name, tag, categoryId }]
    courses: { type: Array, required: true },
    // [{ id, name }]
    categories: { type: Array, default: () => [] },
    // [{ id, name }]
    selected: { type: Array, default: () => [] }
  },
  data() {
    return {
      openCategoryId: null,
      search: ""
    };
  },
  computed: {
    groups() {
      return groupCoursesByCategory(this.courses, this.categories);
    },
    // One group (no categories, or they could not be loaded) needs no tiles:
    // its courses are shown straight away.
    hasCategories() {
      return this.groups.length > 1;
    },
    searchText() {
      return (this.search || "").trim();
    },
    searching() {
      return this.searchText.length > 0;
    },
    openGroup() {
      if (!this.hasCategories) return this.groups[0] || null;
      return this.groups.find(g => g.id === this.openCategoryId) || null;
    },
    showingCategories() {
      return this.hasCategories && !this.searching && !this.openGroup;
    },
    visibleCourses() {
      if (this.searching) {
        const q = this.searchText.toLowerCase();
        return this.courses.filter(c => (c.name || "").toLowerCase().indexOf(q) !== -1);
      }
      return this.openGroup ? this.openGroup.courses : [];
    },
    selectedIds() {
      const ids = {};
      this.selected.forEach(c => {
        ids[c.id] = true;
      });
      return ids;
    },
    groupOfCourse() {
      const map = {};
      this.groups.forEach(g => {
        g.courses.forEach(c => {
          map[c.id] = g.name;
        });
      });
      return map;
    },
    hint() {
      const total = this.courses.length;
      if (this.searching) {
        return `Showing ${this.visibleCourses.length} of ${total} courses in your library.`;
      }
      if (this.showingCategories) {
        return `Pick a category to see its courses, then tick every course you want to assign in the next step. ${total} courses in ${this.groups.length} categories.`;
      }
      return "Tick every course you want to assign in the next step.";
    }
  },
  methods: {
    selectedIn(group) {
      return group.courses.filter(c => this.selectedIds[c.id]).length;
    },
    categoryName(course) {
      return this.groupOfCourse[course.id] || "Course";
    },
    countLabel(n) {
      return n === 1 ? "1 course" : `${n} courses`;
    }
  }
};
</script>

<style scoped>
.course-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.course-search {
  max-width: 360px;
}

.course-toolbar__count {
  font-size: 13px;
  color: #42505e;
  white-space: nowrap;
}

.picker-link {
  background: none;
  border: 0;
  padding: 0;
  color: #08a0a8;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.picker-link:hover {
  text-decoration: underline;
}

.picker-hint {
  margin: 16px 0 0;
  font-size: 13px;
  color: #7a8a9a;
  line-height: 1.45;
}

.category-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
}

.category-tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  text-align: left;
  border: 1px solid #e8eef2;
  border-radius: 12px;
  padding: 16px 18px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.category-tile:hover,
.category-tile:focus-visible {
  border-color: #08a0a8;
  box-shadow: 0 0 0 3px rgba(8, 160, 168, 0.2);
  outline: none;
}

.category-tile__name {
  font-size: 15px;
  font-weight: 700;
  color: #002458;
}

.category-tile__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-height: 20px;
}

.category-tile__count {
  font-size: 12px;
  color: #7a8a9a;
}

.category-tile__selected {
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #002458;
  background: rgba(123, 200, 163, 0.25);
  padding: 2px 8px;
  border-radius: 999px;
}

.category-head {
  display: flex;
  align-items: baseline;
  gap: 16px;
  margin-bottom: 12px;
}

.category-head__title {
  margin: 0;
  font-size: 16px;
  color: #002458;
}

.category-head__title span {
  font-weight: 400;
  color: #7a8a9a;
}

/* Six cards in view (three rows of two), the rest scroll. */
.course-scroll {
  max-height: 456px;
  overflow-y: auto;
  padding: 4px 6px 4px 4px;
  margin: 0 -4px;
  border-radius: 12px;
}

.course-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}

.course-card {
  position: relative;
  border: 1px solid #e8eef2;
  border-radius: 12px;
  padding: 18px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  background: #fff;
}

.course-card:hover,
.course-card.is-selected {
  border-color: #08a0a8;
  box-shadow: 0 0 0 3px rgba(8, 160, 168, 0.2);
}

.course-card__badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #002458;
  background: rgba(123, 200, 163, 0.25);
  padding: 3px 8px;
  border-radius: 999px;
  margin-bottom: 10px;
}

.course-card h3 {
  margin: 0 0 4px;
  font-size: 15px;
  color: #002458;
}

.course-card p {
  margin: 0;
  font-size: 12px;
  color: #7a8a9a;
}

.course-card__check {
  position: absolute;
  top: 14px;
  right: 14px;
}

@media (max-width: 960px) {
  .course-grid {
    grid-template-columns: 1fr;
  }
}
</style>
